import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Play, Terminal, Shield, AlertTriangle, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ActionCenterPage() {
  const { authAxios } = useAuth();
  const [playbooks, setPlaybooks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [runParams, setRunParams] = useState({});
  const [manualInputs, setManualInputs] = useState({}); // Track which params are in manual mode
  const [dryRun, setDryRun] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const selectedServer = useMemo(() => {
    if (!runParams.asset_id) return null;
    return servers.find((s) => s.asset_id === runParams.asset_id) || null;
  }, [servers, runParams.asset_id]);

  const parseMemoryTotalGb = (memoryUsedPct) => {
    const s = String(memoryUsedPct || '').trim();
    if (!s.includes('/')) return null;
    const totalPart = s.split('/')[1].trim();
    const m = totalPart.match(/^([0-9]+(?:\.[0-9]+)?)\s*([a-zA-Z]+)$/);
    if (!m) return null;
    const val = Number(m[1]);
    if (!Number.isFinite(val)) return null;
    const unit = m[2].toLowerCase();
    if (unit === 'g' || unit === 'gb' || unit === 'gi' || unit === 'gib') return val;
    if (unit === 'm' || unit === 'mb' || unit === 'mi' || unit === 'mib') return val / 1024;
    if (unit === 'k' || unit === 'kb' || unit === 'ki' || unit === 'kib') return val / (1024 * 1024);
    if (unit === 't' || unit === 'tb' || unit === 'ti' || unit === 'tib') return val * 1024;
    return null;
  };

  const swapRecommendation = useMemo(() => {
    if (selectedPlaybook?.id !== 'setup-swapfile') return null;
    const totalGb = parseMemoryTotalGb(selectedServer?.audit?.memory_used_pct);
    const gb = totalGb && totalGb > 0 ? totalGb : null;
    const recommendedGb = gb == null ? 2 : (gb <= 4 ? 2 : (gb <= 16 ? 4 : 8));
    const options = Array.from(new Set([recommendedGb, 1, 2, 4, 8, 16, 32].filter((x) => x >= 1 && x <= 64)));
    options.sort((a, b) => a - b);
    return { totalGb: gb, recommendedGb, options };
  }, [selectedPlaybook?.id, selectedServer]);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const [pbRes, runsRes, srvRes] = await Promise.all([
        api.get('/playbooks'),
        api.get('/action-runs/latest'),
        api.get('/servers')
      ]);
      setPlaybooks(pbRes.data);
      setRuns(runsRes.data);
      setServers(srvRes.data);
    } catch (err) {
      toast.error('Failed to fetch action center data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRun = async () => {
    if (!selectedPlaybook) return;
    setExecuting(true);
    setRunResult(null);
    try {
      const api = authAxios();
      const payload = {
        playbook_id: selectedPlaybook.id,
        asset_id: runParams.asset_id, // Special param
        params: runParams,
        dry_run: dryRun
      };
      const res = await api.post('/playbooks/run', payload);
      setRunResult(res.data);
      if (res.data?.status === 'success') {
        if (!dryRun) toast.success('Playbook executed');
        else toast.info('Dry run complete');
      } else {
        const msg = res.data?.error || res.data?.detail || (res.data?.stderr ? String(res.data.stderr).slice(-200) : '') || 'Execution failed';
        toast.error(msg);
      }
      
      // Refresh logs
      const runsRes = await api.get('/action-runs/latest');
      setRuns(runsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
          Action Center
        </h1>
        <p className="text-sm text-[#a1a1aa] mt-1">
          Execute safe maintenance tasks
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {playbooks.map((pb) => (
          <Card key={pb.id} className="bg-[#121214] border-white/10 hover:border-[#EF694C]/50 transition-colors">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-[#EF694C]" />
                  <h3 className="font-semibold text-white">{pb.name}</h3>
                </div>
                {pb.requires_sudo && (
                  <Badge variant="outline" className="border-red-500/20 text-red-500 bg-red-500/5 text-[10px]">
                    SUDO
                  </Badge>
                )}
              </div>
              <p className="text-sm text-[#a1a1aa] min-h-[40px]">{pb.description}</p>
              <div className="bg-black/20 p-2 rounded text-xs font-mono text-[#52525b] truncate">
                {pb.command_template}
              </div>
              <Button 
                className="w-full bg-white/5 hover:bg-white/10 text-white"
                onClick={() => {
                  setSelectedPlaybook(pb);
                  setRunParams({});
                  setManualInputs({});
                  setRunResult(null);
                }}
              >
                <Play className="w-4 h-4 mr-2" />
                Configure Run
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
        {runs.map((run) => (
          <div key={run.id || run._id} className="bg-[#121214] border border-white/10 p-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${run.status === 'success' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                {run.status === 'success' ? <Shield className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              </div>
              <div>
                <p className="text-white font-medium">{run.playbook_id} on {run.asset_id}</p>
                <p className="text-xs text-[#52525b]">{new Date(run.ts).toLocaleString()} by {run.user}</p>
              </div>
            </div>
            <Badge variant="outline" className="border-white/10 text-[#a1a1aa]">
              {run.duration_ms}ms
            </Badge>
          </div>
        ))}
      </div>

      <Dialog open={!!selectedPlaybook} onOpenChange={() => setSelectedPlaybook(null)}>
        <DialogContent className="bg-[#18181b] border-white/10 text-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Run Playbook: {selectedPlaybook?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Target Asset</Label>
              <Select 
                value={runParams.asset_id || ''} 
                onValueChange={(val) => {
                  const next = { ...runParams, asset_id: val };
                  if (selectedPlaybook?.id === 'setup-swapfile') {
                    const srv = servers.find((s) => s.asset_id === val) || null;
                    const totalGb = parseMemoryTotalGb(srv?.audit?.memory_used_pct);
                    const gb = totalGb && totalGb > 0 ? totalGb : null;
                    const recommendedGb = gb == null ? 2 : (gb <= 4 ? 2 : (gb <= 16 ? 4 : 8));
                    next.swap_gb = String(recommendedGb);
                  }
                  setRunParams(next);
                }}
              >
                <SelectTrigger className="bg-[#121214] border-white/10">
                  <SelectValue placeholder="Select a server..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-white/10 text-white max-h-[300px]">
                  {servers.map(s => (
                    <SelectItem key={s.asset_id} value={s.asset_id}>
                      {s.meta?.alias || s.asset_id} ({s.host})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedPlaybook?.params?.map(p => {
              if (p === 'swap_gb' && selectedPlaybook?.id === 'setup-swapfile') {
                const rec = swapRecommendation;
                return (
                  <div key={p} className="space-y-2">
                    <Label className="capitalize">{p}</Label>
                    <Select
                      value={String(runParams[p] || (rec?.recommendedGb ?? 2))}
                      onValueChange={(val) => setRunParams({ ...runParams, [p]: val })}
                    >
                      <SelectTrigger className="bg-[#121214] border-white/10 text-white">
                        <SelectValue placeholder="Select swap size..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-white/10 text-white">
                        {(rec?.options || [1, 2, 4, 8, 16, 32]).map((gb) => (
                          <SelectItem key={gb} value={String(gb)}>
                            {gb} GB{rec?.recommendedGb === gb ? ' (empfohlen)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-[#a1a1aa]">
                      {rec?.totalGb ? `Empfehlung basiert auf RAM ~${rec.totalGb.toFixed(1)} GB` : 'Empfehlung: 2 GB (RAM nicht verfügbar)'}
                    </div>
                  </div>
                );
              }

              // Special handling for 'domain' parameter if asset is selected
              if (p === 'domain' && runParams.asset_id) {
                const server = servers.find(s => s.asset_id === runParams.asset_id);
                const domains = server?.audit?.domains || [];
                
                if (domains.length > 0 && !manualInputs[p]) {
                  return (
                    <div key={p} className="space-y-2">
                      <Label className="capitalize">{p}</Label>
                      <Select 
                        value={runParams[p] || ''} 
                        onValueChange={(val) => {
                          if (val === 'manual_input') {
                            setManualInputs({...manualInputs, [p]: true});
                            setRunParams({...runParams, [p]: ''});
                          } else {
                            setRunParams({...runParams, [p]: val});
                          }
                        }}
                      >
                        <SelectTrigger className="bg-[#121214] border-white/10 text-white">
                          <SelectValue placeholder="Select a domain..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121214] border-white/10 text-white">
                          {domains.map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                          <SelectItem value="manual_input" className="text-[#EF694C] font-semibold">Enter manually...</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
              }

              // Default input (or manual override)
              return (
                <div key={p} className="space-y-2">
                  <Label className="capitalize flex justify-between">
                    {p}
                    {(p === 'domain' && manualInputs[p]) && (
                      <button 
                        onClick={() => setManualInputs({...manualInputs, [p]: false})}
                        className="text-xs text-[#EF694C] hover:underline"
                      >
                        Back to list
                      </button>
                    )}
                  </Label>
                  <Input 
                    placeholder={`Value for ${p}`}
                    value={runParams[p] || ''}
                    onChange={e => setRunParams({...runParams, [p]: e.target.value})}
                    className="bg-[#121214] border-white/10"
                  />
                </div>
              );
            })}

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="dry-run" 
                checked={dryRun} 
                onCheckedChange={setDryRun}
                className="border-white/20 data-[state=checked]:bg-[#EF694C] data-[state=checked]:border-[#EF694C]"
              />
              <Label htmlFor="dry-run" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Dry Run (Preview command only)
              </Label>
            </div>

            {runResult && (
              <div className="mt-4 p-3 bg-black/40 rounded-lg space-y-2">
                <p className={`text-xs font-bold ${runResult.status === 'success' || runResult.status === 'dry_run' ? 'text-emerald-500' : 'text-red-500'}`}>
                  Result: {runResult.status}
                </p>
                <pre className="text-[10px] font-mono text-[#a1a1aa] whitespace-pre-wrap overflow-x-auto max-h-[300px] overflow-y-auto">
                  {runResult.stdout || runResult.stderr || runResult.command || 'No output'}
                </pre>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectedPlaybook(null)}>Cancel</Button>
            <Button 
              onClick={handleRun} 
              disabled={executing}
              className="bg-[#EF694C] hover:bg-[#d65d43] text-white"
            >
              {executing ? 'Executing...' : dryRun ? 'Preview' : 'Execute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
